/**
 * Author: Gaurav
 *
 * Component: Unauthorized
 * Description:
 * - Unauthorized access view that informs users they are not authorized and provides an option to log out and retry authentication.
 */

import { Button as HexaButton } from '@abi-labs-hexa/web-v2/components/button';
import { useMsal } from '@azure/msal-react';
import { Box, Heading, Image, Text, VStack } from '@chakra-ui/react';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import bgLogin from 'watchtower-ui/assets/images/bg_login.png';
import footerLogo from 'watchtower-ui/assets/images/loginFooterLogo.png';
import logo from 'watchtower-ui/assets/images/logoWT.svg';
import yellowBar from 'watchtower-ui/assets/images/yelloBar.svg';
import AccessRequestForm from 'watchtower-ui/components/AccessRequestForm/AccessRequestForm';
import 'watchtower-ui/locales/i18n';
import buttonStyles from 'watchtower-ui/styles/common/button.module.scss';

const Unauthorized: FC<object> = () => {
  const { t } = useTranslation();
  const msalDetails = useMsal();
  const [isAccessFormOpen, setIsAccessFormOpen] = useState(false);
  const [errorType, setErrorType] = useState<string | null>(null);

  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      setErrorType(urlParams.get('errorType'));
    } catch {
      setErrorType(null);
    }
  }, []);

  const isTechnicalError = errorType === 'technical';

  const getErrorMessage = () => {
    if (isTechnicalError) {
      return t('common:technicalError');
    }
    return t('common:notAuthorised');
  };

  const getErrorTitle = () => {
    if (isTechnicalError) {
      return t('common:serviceUnavailable');
    }
    return t('common:requestAccess');
  };

  const handleLogout = () => {
    // eslint-disable-next-line no-void -- Need to tell it to explicitly ignore the result of this function
    void msalDetails.instance.logout();
  };

  const handleRequestAccess = () => {
    setIsAccessFormOpen(true);
  };
  return (
    <Box
      overflow="auto"
      display="flex"
      height="100%"
      width="100%"
      position="fixed"
      alignItems="center"
      justifyContent="center"
      background={`url(${bgLogin})`}
      backgroundAttachment="fixed"
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center center"
      padding="30px 20px"
      data-testid="unauthorized-container"
    >
      <Box
        textAlign="center"
        color="white"
        backgroundColor="whitealpha.200"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="gray.500"
        borderRadius="14px"
        padding="16px"
        width="100%"
        maxWidth="320px"
        minHeight="480px"
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        data-testid="content-box"
      >
        <Image width="80px" src={logo} alt="WatchTower Logo" marginX="auto" />
        <Heading color="white" as="h2" size="heading5" fontWeight="600" marginTop="10px">
          {t('common:watchtower')}
        </Heading>
        <Heading color="white" as="h1" size="heading3" fontWeight="700" marginTop="40px" marginBottom="0">
          {getErrorTitle()}
        </Heading>
        <Text color="white" fontFamily="'Open Sans', 'Avant', 'light', sans-serif" marginY="40px" marginX="0">
          {getErrorMessage()}
        </Text>
        <VStack spacing={4} width="100%">
          {!isTechnicalError && (
            <HexaButton
              $variant="primary"
              className={buttonStyles.outline}
              onClick={handleRequestAccess}
              data-testid="request-access-button"
              style={{ width: '100%' }}
            >
              {t('common:requestAccess')}
            </HexaButton>
          )}
          <HexaButton
            $variant="destructive"
            className={buttonStyles.outline}
            onClick={handleLogout}
            data-testid="logout-button"
            style={{ width: '100%' }}
          >
            {isTechnicalError ? t('common:retry') : t('common:logoutAndRetry')}
          </HexaButton>
        </VStack>
        <Image
          position="absolute"
          bottom="-7px"
          left="50%"
          width="120px"
          transform="translateX(-50%)"
          src={yellowBar}
          alt="yellowBar"
        />
      </Box>
      <Image
        width="100%"
        maxWidth="350px"
        position="fixed"
        bottom="20px"
        left="20px"
        src={footerLogo}
        alt="loginFootLogo"
      />
      <AccessRequestForm isOpen={isAccessFormOpen} onClose={() => setIsAccessFormOpen(false)} />
    </Box>
  );
};
export default Unauthorized;
